import React, { useState, useEffect } from 'react';
import { Card, ButtonGroup, Button, Badge, Alert } from 'react-bootstrap';

const CarExpertizMap = ({
    initialExpertizData = {},
    onChange,
    disabled = false,
    showLegend = true
}) => {
    const [selectedParts, setSelectedParts] = useState(new Map());
    const [selectedStatus, setSelectedStatus] = useState('original');
    const [hoveredPart, setHoveredPart] = useState(null);

    // ✅ Parça durumları - TAM RENKLER
    const statusConfig = {
        original: { color: '#16A34A', label: 'Orijinal', icon: '✓', bgColor: '#16A34A' }, // KOYU YEŞİL
        painted: { color: '#EAB308', label: 'Boyalı', icon: '🎨', bgColor: '#EAB308' }, // TAM SARI
        local_paint: { color: '#2563EB', label: 'Lokal Boya', icon: '🔵', bgColor: '#2563EB' }, // TAM MAVİ
        replaced: { color: '#DC2626', label: 'Değişen', icon: '🔧', bgColor: '#DC2626' } // TAM KIRMIZI
    };

    // ✅ 13 PARÇANIN TALİMATLARA GÖRE DOĞRU POZİSYONLARI
    const carParts = [
        { id: 'rear_left_fender', name: 'Sol Arka Çamurluk', top: '15%', left: '16.9%', width: '5%', height: '7.5%' },
        { id: 'rear_left_door', name: 'Sol Arka Kapı', top: '15%', left: '37.8%', width: '5%', height: '7.5%' },
        { id: 'front_left_door', name: 'Sol Ön Kapı', top: '15%', left: '52.3%', width: '5%', height: '7.5%' },
        { id: 'front_left_fender', name: 'Sol Ön Çamurluk', top: '18%', left: '77.5%', width: '5%', height: '7.5%' },

       // { id: 'rear_bumper' },
        { id: 'trunk', name: 'Bagaj', top: '47%', left: '16.5%', width: '5%', height: '7.5%' },
        { id: 'roof', name: 'Tavan', top: '47%', left: '42%', width: '5%', height: '7.5%' },
        { id: 'hood', name: 'Kaput', top: '47.5%', left: '73%', width: '5%', height: '7.5%' },
      //  { id: 'front_bumper'},

        { id: 'rear_right_fender', name: 'Sağ Arka Çamurluk', top: '78%', left: '16%', width: '5%', height: '7.5%' },
        { id: 'rear_right_door', name: 'Sağ Arka Kapı', top: '79.2%', left: '38.2%', width: '5%', height: '7.5%' },
        { id: 'front_right_door', name: 'Sağ Ön Kapı', top: '79.2%', left: '53%', width: '5%', height: '7.5%' },
        { id: 'front_right_fender', name: 'Sağ Ön Çamurluk', top: '76%', left: '79%', width: '5%', height: '7.5%' }
    ];

    // Bu useEffect'i şöyle değiştirin:
    useEffect(() => {
        console.log('🔍 CAR EXPERTIZ MAP RECEIVED:', {
            initialExpertizData,
            hasInitialData: !!initialExpertizData,
            initialDataKeys: Object.keys(initialExpertizData || {}),
            initialDataValues: Object.values(initialExpertizData || {}),
            initialDataType: typeof initialExpertizData
        });

        if (initialExpertizData) {
            const newMap = new Map();
            Object.entries(initialExpertizData).forEach(([key, value]) => {
                newMap.set(key, value);
            });
            setSelectedParts(newMap);

            console.log('🔍 CAR EXPERTIZ MAP - AFTER SETTING MAP:', {
                selectedPartsSize: newMap.size,
                selectedPartsEntries: Array.from(newMap.entries())
            });
        } else {
            console.log('🔍 CAR EXPERTIZ MAP - NO INITIAL DATA, KEEPING DEFAULTS');
        }
    }, [initialExpertizData]);

    const handlePartClick = (partId) => {
        if (disabled) return;
        const newParts = new Map(selectedParts);
        newParts.set(partId, selectedStatus);
        setSelectedParts(newParts);
        if (onChange) {
            onChange(Object.fromEntries(newParts));
        }
    };

    const getPartStatus = (partId) => selectedParts.get(partId) || 'original';

    const getStatusCounts = () => {
        const counts = { original: 0, painted: 0, local_paint: 0, replaced: 0 };
        carParts.forEach(part => {
            const status = getPartStatus(part.id);
            counts[status]++;
        });
        return counts;
    };

    const statusCounts = getStatusCounts();

    return (
        <Card className="expertiz-map-card">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">🔍 Expertiz Durumu</h6>
                    <small className="text-muted">
                        {Object.values(statusCounts).reduce((a, b) => a + b, 0) - statusCounts.original} parça işaretlendi
                    </small>
                </div>
            </Card.Header>

            <Card.Body>
                {/* Status Selection */}
                <div className="mb-3">
                    <small className="text-muted d-block mb-2">Durum seçin, sonra parçaya tıklayın:</small>
                    <ButtonGroup size="sm" className="w-100">
                        {Object.entries(statusConfig).map(([status, config]) => (
                            <Button
                                key={status}
                                variant={selectedStatus === status ?
                                    (status === 'painted' ? 'warning' :
                                        status === 'local_paint' ? 'primary' :
                                            status === 'replaced' ? 'danger' : 'success') : 'outline-secondary'}
                                onClick={() => setSelectedStatus(status)}
                                disabled={disabled}
                            >
                                <span className="me-1">{config.icon}</span>
                                {config.label}
                            </Button>
                        ))}
                    </ButtonGroup>
                </div>

                {/* ✅ YENİ CAR MAP - car-side.png */}
                <div
                    className="position-relative mx-auto mb-3"
                    style={{
                        width: '100%',
                        maxWidth: '600px',
                        aspectRatio: '3/2',
                        backgroundColor: '#F8F9FA',
                        borderRadius: '12px',
                        backgroundImage: `url("/images/car-side.png")`,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center'
                    }}
                >
                    {/* ✅ TIKLANABİLİR OVERLAY DIV'LER */}
                    {carParts.map(part => {
                        const status = getPartStatus(part.id);
                        const config = statusConfig[status];

                        return (
                            <div
                                key={part.id}
                                onClick={() => handlePartClick(part.id)}
                                onMouseEnter={() => setHoveredPart(part)}
                                onMouseLeave={() => setHoveredPart(null)}
                                style={{
                                    position: 'absolute',
                                    top: part.top,
                                    left: part.left,
                                    width: part.width,
                                    height: part.height,
                                    minWidth: '20px',
                                    minHeight: '20px',
                                    maxWidth: '40px',
                                    maxHeight: '40px',
                                    backgroundColor: config.color,
                                    border: `2px solid ${config.bgColor}`,
                                    borderRadius: '50%',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                    zIndex: 10,
                                    transform: hoveredPart?.id === part.id ? 'scale(1.2)' : 'scale(1)',
                                    aspectRatio: '1/1'
                                }}
                                className={`${hoveredPart?.id === part.id ? 'shadow' : ''}`}
                                title={`${part.name} - ${config.label}`}
                            />
                        );
                    })}

                    {/* Hover Tooltip */}
                    {hoveredPart && !disabled && (
                        <div
                            className="position-absolute bg-dark text-white px-2 py-1 rounded small"
                            style={{
                                top: '5px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 1000,
                                pointerEvents: 'none',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {hoveredPart.name} - {statusConfig[getPartStatus(hoveredPart.id)].label}
                        </div>
                    )}
                </div>

                {/* Status Summary */}
                {showLegend && (
                    <div className="row g-2">
                        {Object.entries(statusConfig).map(([status, config]) => (
                            <div key={status} className="col-6 col-md-3">
                                <div className="d-flex align-items-center justify-content-between small p-2 rounded"
                                    style={{ backgroundColor: '#F8F9FA' }}>
                                    <div className="d-flex align-items-center">
                                        <div style={{
                                            width: '12px', height: '12px',
                                            backgroundColor: config.bgColor,
                                            borderRadius: '2px'
                                        }} className="me-2"></div>
                                        <span>{config.label}</span>
                                    </div>
                                    <Badge bg="secondary">{statusCounts[status] || 0}</Badge>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default CarExpertizMap;